import React from 'react'
import {
  Target,
  TrendingUp,
  AlertCircle,
  Lightbulb,
  Flame,
  ArrowUpRight,
  Star,
  Shield,
  Zap,
  Sparkles
} from 'lucide-react'

// 趋势卡片组件
const TrendCard: React.FC<{
  title: string
  status: 'hot' | 'rising' | 'stable'
  icon?: string
}> = ({ title, status }) => {
  const config = {
    hot: {
      bg: 'bg-gradient-to-r from-rose-50 to-orange-50',
      border: 'border-rose-200',
      badge: 'bg-rose-100 text-rose-700 border-rose-200',
      label: '🔥 热门',
      icon: <Flame className="w-4 h-4" />
    },
    rising: {
      bg: 'bg-gradient-to-r from-blue-50 to-indigo-50',
      border: 'border-blue-200',
      badge: 'bg-blue-100 text-blue-700 border-blue-200',
      label: '📈 上升',
      icon: <TrendingUp className="w-4 h-4" />
    },
    stable: {
      bg: 'bg-gradient-to-r from-purple-50 to-violet-50',
      border: 'border-purple-200',
      badge: 'bg-purple-100 text-purple-700 border-purple-200',
      label: '⭐ 稳定',
      icon: <Star className="w-4 h-4" />
    }
  }

  const c = config[status]

  return (
    <div
      className={`flex items-center justify-between p-4 ${c.bg} rounded-xl border ${c.border} hover:shadow-md transition-all`}
    >
      <span className="font-semibold text-slate-800">{title}</span>
      <span
        className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-sm font-bold ${c.badge} border`}
      >
        {c.icon}
        {c.label}
      </span>
    </div>
  )
}

export const TeacherMarketInsights: React.FC = () => {
  return (
    <div className="space-y-8">
      {/* 头部 */}
      <div className="relative rounded-3xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtNi42MjcgMC0xMiA1LjM3My0xMiAxMnM1LjM3MyAxMiAxMiAxMiAxMi01LjM3MyAxMi0xMi01LjM3My0xMi0xMi0xMnptMCAxOGMtMy4zMTQgMC02LTIuNjg2LTYtNnMyLjY4Ni02IDYtNiA2IDIuNjg2IDYgNi0yLjY4NiA2LTYgNnoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjEiLz48L2c+PC9zdmc+')] opacity-20" />
        <div className="relative px-8 py-10 text-center">
          <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Target className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">市场洞察报告</h2>
          <p className="text-white/80">深度市场洞察，把握教育行业脉搏</p>
        </div>
      </div>

      {/* 主要内容 */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* 热门趋势 */}
        <div className="bg-white rounded-3xl border border-slate-100 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center text-white shadow-lg">
              <TrendingUp className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">热门趋势</h3>
              <p className="text-sm text-slate-500">市场最新动向</p>
            </div>
          </div>
          <div className="space-y-3">
            <TrendCard title="AI编程教育" status="hot" />
            <TrendCard title="数学思维训练" status="rising" />
            <TrendCard title="在线英语口语" status="stable" />
          </div>
        </div>

        {/* 风险预警 */}
        <div className="bg-white rounded-3xl border border-slate-100 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-rose-400 to-red-500 rounded-xl flex items-center justify-center text-white shadow-lg">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">风险预警</h3>
              <p className="text-sm text-slate-500">潜在风险提示</p>
            </div>
          </div>
          <div className="space-y-3">
            <div className="p-4 bg-gradient-to-r from-rose-50 to-red-50 rounded-xl border border-rose-200 hover:shadow-md transition-all">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-4 h-4 text-rose-600" />
                <span className="font-bold text-rose-800">价格竞争加剧</span>
              </div>
              <p className="text-sm text-rose-600">英语类课程平均价格下降 15%</p>
            </div>
            <div className="p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl border border-amber-200 hover:shadow-md transition-all">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-4 h-4 text-amber-600" />
                <span className="font-bold text-amber-800">需求季节性波动</span>
              </div>
              <p className="text-sm text-amber-600">暑期需求预计增长 80%</p>
            </div>
          </div>
        </div>
      </div>

      {/* 本周重点洞察 */}
      <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-3xl border border-indigo-100 p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white shadow-lg">
            <Sparkles className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">本周重点洞察</h3>
            <p className="text-sm text-slate-500">AI 为您总结的关键信息</p>
          </div>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="bg-white rounded-2xl p-5 border border-indigo-100">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-5 h-5 text-indigo-600" />
              <h4 className="font-bold text-indigo-800">机会窗口</h4>
            </div>
            <ul className="space-y-2">
              {[
                '青少年Python编程需求激增',
                '小学数学思维课程供不应求',
                '成人职业技能培训兴起'
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-indigo-700">
                  <ArrowUpRight className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-500" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-indigo-100">
            <div className="flex items-center gap-2 mb-4">
              <Lightbulb className="w-5 h-5 text-amber-600" />
              <h4 className="font-bold text-amber-800">行动建议</h4>
            </div>
            <ul className="space-y-2">
              {['考虑开设Python入门课程', '优化课程定价策略', '准备暑期课程计划'].map(
                (item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-amber-700">
                    <ArrowUpRight className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-500" />
                    {item}
                  </li>
                )
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
